import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import "./ImageUploader.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next"; // Import translation hook

// Get API base URL from environment variables
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.aspose.ai";

const SESSION_URL = `${API_BASE_URL}/total/api-image-translator/session`;

/** Uses limit_per_hour / limit_scope from 429 JSON when present (matches backend env). */
const rateLimitBannerMessage = (err, t, contextScope) => {
  const data = err.response?.data ?? {};
  const raw = data.limit_per_hour;
  const n =
    typeof raw === "string" ? parseInt(raw, 10) : raw;
  let scope = data.limit_scope;
  if (!scope || scope === "api") {
    scope = contextScope;
  }
  if (typeof n === "number" && Number.isFinite(n)) {
    if (scope === "upload") {
      return t("app.alerts.rateLimitUpload", { count: n });
    }
    if (scope === "translate") {
      return t("app.alerts.rateLimitTranslate", { count: n });
    }
    return t("app.alerts.rateLimitGeneric", { count: n });
  }
  return t("app.alerts.rateLimitFallback");
};

// Function to get browser default language and map it to available languages
const getBrowserDefaultLanguage = (availableLanguages) => {
  const browserLang = navigator.language || navigator.userLanguage;
  // en-US → langCode: 'en' → Returns
  // fr-FR → langCode: 'fr' → Returns
  const langCode = browserLang.split("-")[0].toLowerCase();

  // Special case for Chinese since languages in translation.json does not have "zh".
  if (langCode === "zh") {
    return availableLanguages["zh-Hans"] || "English";
  }

  // Check if the browser language code exists in available languages
  if (availableLanguages[langCode]) {
    return availableLanguages[langCode];
  }

  // Fallback to English if no match found
  return "English";
};

// Updated header detection that checks for your specific header first
const getHeaderHeight = () => {
  // Check for your specific header structure first
  const header =
    document.querySelector(".topheader") || // Most specific
    document.querySelector(".topsection") || // Backup
    document.querySelector('[class*="header"]') || // Generic header classes
    document.querySelector('[class*="menu"]') || // Menu-based headers
    document.querySelector("header") || // HTML5 header tag
    document.querySelector("nav"); // Navigation elements

  if (header) {
    return header.offsetHeight;
  }

  return 80; // Default fallback
};

const scrollToElementWithDynamicOffset = (
  elementRef,
  additionalOffset = 20
) => {
  if (elementRef.current) {
    const headerHeight = getHeaderHeight();
    const elementPosition = elementRef.current.offsetTop;
    const offsetPosition = elementPosition - headerHeight - additionalOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

const ImageUploader = () => {
  const { t } = useTranslation(); // Translation function

  // Get available languages from translation
  const availableLanguages = t("app.languages", { returnObjects: true });

  // Set default target language based on browser language
  const defaultTargetLanguage = getBrowserDefaultLanguage(availableLanguages);

  const [selectedFile, setSelectedFile] = useState(null);
  const [response, setResponse] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [targetLanguage, setTargetLanguage] = useState(defaultTargetLanguage);
  const [translatedText, setTranslatedText] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [rateLimitBanner, setRateLimitBanner] = useState(null);

  const extractedTextRef = useRef(null); // Ref for extracted text section
  const translatedTextRef = useRef(null); // Ref for translated text section
  const sessionTokenRef = useRef(null);
  /** Deduplicate concurrent GET /session (mount + upload/translate) to one in-flight request. */
  const sessionFetchPromiseRef = useRef(null);

  const fetchSession = useCallback(async () => {
    if (sessionFetchPromiseRef.current) {
      await sessionFetchPromiseRef.current;
      return;
    }
    const p = axios.get(SESSION_URL).then((res) => {
      sessionTokenRef.current = res.data.token;
    });
    sessionFetchPromiseRef.current = p;
    try {
      await p;
    } finally {
      sessionFetchPromiseRef.current = null;
    }
    // Do not clear rateLimitBanner here: clear on successful upload/translate only.
  }, []);

  const authHeaders = () => {
    const token = sessionTokenRef.current;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  /** 401 → refresh session → one retry. 429 on either attempt sets the banner (including retry). */
  const runWithAuthRetry = useCallback(
    async (doRequest, scope) => {
      try {
        return await doRequest();
      } catch (err) {
        if (err.response?.status === 401) {
          sessionTokenRef.current = null;
          await fetchSession();
          try {
            return await doRequest();
          } catch (err2) {
            if (err2.response?.status === 429) {
              setRateLimitBanner(rateLimitBannerMessage(err2, t, scope));
            }
            throw err2;
          }
        }
        if (err.response?.status === 429) {
          setRateLimitBanner(rateLimitBannerMessage(err, t, scope));
        }
        throw err;
      }
    },
    [fetchSession, t]
  );

  /** Always refresh before protected calls so the token matches the server (avoids stale ref + races with mount). */
  const ensureSession = useCallback(async () => {
    await fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    fetchSession().catch((err) => {
      console.error("Failed to obtain API session token", err);
    });
  }, [fetchSession]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert(t("app.alerts.onlyImageAllowed"));
      event.target.value = ""; // Reset the input
      return;
    }

    setSelectedFile(file);
    handleUpload(file);
  };

  const handleTextChange = (event) => {
    setEditedText(event.target.value);
  };

  const handleTargetLanguageChange = (event) => {
    setTargetLanguage(event.target.value);
  };

  const handleUpload = async (file) => {
    if (isUploading) return; // Prevent multiple uploads at the same time.
    setIsUploading(true);

    const uploadFile = file || selectedFile;
    if (!uploadFile) {
      setIsUploading(false);
      return;
    }

    // TranslatedText section should be hidden each time a user clicks on the "Choose Image" button.
    setTranslatedText("");
    const formData = new FormData();
    formData.append("file", uploadFile);

    const doUpload = () =>
      axios.post(
        `${API_BASE_URL}/total/api-image-translator/upload/`,
        formData,
        {
          headers: {
            ...authHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );

    try {
      await ensureSession();
      const res = await runWithAuthRetry(doUpload, "upload");
      setRateLimitBanner(null);
      setResponse(res.data);
      setEditedText(res.data.image_text);

      // Scroll to extracted text section
      setTimeout(() => {
        scrollToElementWithDynamicOffset(extractedTextRef);
      }, 100);
    } catch (error) {
      if (error.response?.status === 429) {
        setRateLimitBanner(rateLimitBannerMessage(error, t, "upload"));
      } else {
        console.error("Error uploading file", error);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleTranslate = async () => {
    if (!editedText) {
      return;
    }

    setIsTranslating(true);
    // Hide TranslatedText section.
    setTranslatedText("");
    const doTranslate = () =>
      axios.post(
        `${API_BASE_URL}/total/api-image-translator/translate/`,
        {
          text: editedText,
          target_language: targetLanguage,
        },
        { headers: authHeaders() }
      );

    try {
      await ensureSession();
      const res = await runWithAuthRetry(doTranslate, "translate");
      setRateLimitBanner(null);
      setTranslatedText(res.data.translated_text);

      // Scroll to translated text section
      setTimeout(() => {
        scrollToElementWithDynamicOffset(translatedTextRef);
      }, 150); // Increase timeout slightly
    } catch (error) {
      if (error.response?.status === 429) {
        setRateLimitBanner(rateLimitBannerMessage(error, t, "translate"));
      } else {
        console.error("Error translating text", error);
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const handleCopy = () => {
    if (!translatedText) return;

    navigator.clipboard
      .writeText(translatedText)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 1500); // Reset after 1.5 sec
      })
      .catch((err) => console.error("Failed to copy text:", err));
  };

  return (
    <div className="uploader-container">
      {rateLimitBanner && (
        <div className="rate-limit-banner" role="alert">
          {rateLimitBanner}
        </div>
      )}
      <div className="file-input-box">
        {isUploading && (
          <div className="translating-container">
            <div className="loader"></div>
            <span>{t("app.uploadSection.fileUpload.extractingText")}</span>
          </div>
        )}
        <input
          type="file"
          id="uploadedFile"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden-file-input"
        />
        <label
          htmlFor="uploadedFile"
          className="file-upload-label btn blue-gradient-button"
        >
          <FontAwesomeIcon
            icon={faUpload}
            aria-label="Upload Icon"
            style={{ color: "white", marginRight: "0.5rem" }}
          />
          {t("app.uploadSection.fileUpload.chooseImage")}
        </label>
      </div>

      {response && (
        <div className="section-box" ref={extractedTextRef}>
          <h3>{t("app.uploadSection.extractedText.title")}</h3>
          <textarea
            className="extracted-text-area"
            value={editedText}
            onChange={handleTextChange}
            rows="8"
          />
          <div className="translation-controls">
            <div className="language-div">
              <label>{t("app.uploadSection.extractedText.to")}</label>
              <select
                className="language-select"
                value={targetLanguage}
                onChange={handleTargetLanguageChange}
              >
                {Object.entries(
                  t("app.languages", { returnObjects: true })
                ).map(([langCode, langName]) => (
                  <option key={langCode} value={langName}>
                    {langName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isTranslating && (
            <div className="translating-container">
              <div className="loader"></div>
              <span>{t("app.uploadSection.extractedText.translating")}</span>
            </div>
          )}

          <button className="translate-button" onClick={handleTranslate}>
            {t("app.uploadSection.extractedText.translateButton")}
          </button>
        </div>
      )}

      {translatedText && (
        <div
          className={`section-box translated-text-container ${
            translatedText ? "show" : ""
          }`}
          ref={translatedTextRef}
        >
          <h3>{t("app.uploadSection.translatedText.title")}</h3>
          <div className="translated-text-content">
            <pre className="translated-text">{translatedText}</pre>
            <button className="copy-button" onClick={handleCopy}>
              <FontAwesomeIcon
                aria-label="Copy Icon"
                icon={copySuccess ? faCheck : faCopy}
              />
              {copySuccess
                ? t("app.uploadSection.translatedText.copied")
                : t("app.uploadSection.translatedText.copy")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
